/**
 * Theme Cache Service
 * 
 * Provides in-memory caching for color themes to minimize API calls.
 */

import { ColorTheme, ColorMapping, ThemeCacheEntry } from '../types/themes';

class ThemeCache {
  private cache: Map<string, ThemeCacheEntry>;
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.cache = new Map();
  }

  /**
   * Generate cache key for a theme
   */
  getCacheKey(dataProduct: string, attribute: string, themeId?: string): string {
    if (themeId) {
      return `${dataProduct}:${attribute}:${themeId}`;
    }
    return `${dataProduct}:${attribute}:default`;
  }

  /**
   * Get theme from cache
   */
  getTheme(dataProduct: string, attribute: string, themeId?: string): ColorTheme | null {
    const key = this.getCacheKey(dataProduct, attribute, themeId);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if cache entry is still valid
    const now = Date.now();
    if (now - entry.cached_at > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }

    return entry.theme;
  }

  /**
   * Store theme in cache
   */
  setTheme(theme: ColorTheme): void {
    const key = this.getCacheKey(theme.data_product, theme.attribute, theme.theme_id);
    this.cache.set(key, {
      theme,
      cached_at: Date.now(),
    });

    // Also cache as default if it's marked as default
    if (theme.is_default) {
      const defaultKey = this.getCacheKey(theme.data_product, theme.attribute);
      this.cache.set(defaultKey, {
        theme,
        cached_at: Date.now(),
      });
    }
  }

  /**
   * Invalidate cache entries
   */
  invalidate(dataProduct?: string, attribute?: string): void {
    if (!dataProduct) {
      // Clear entire cache
      this.cache.clear();
      return;
    }

    if (!attribute) {
      // Clear all entries for a product
      const keysToDelete: string[] = [];
      this.cache.forEach((_, key) => {
        if (key.startsWith(`${dataProduct}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.cache.delete(key));
      return;
    }

    // Clear all entries for a product/attribute
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${dataProduct}:${attribute}:`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get color for a data value using theme logic
   */
  getColorForValue(theme: ColorTheme, value: any): string {
    const DEFAULT_COLOR = '#9ca3af'; // Gray-400

    if (!theme || !theme.mappings || theme.mappings.length === 0) {
      return DEFAULT_COLOR;
    }

    if (theme.mapping_type === 'categorical') {
      // Categorical mapping - exact match
      const mapping = theme.mappings.find(m => m.category === value);
      return mapping ? mapping.color : DEFAULT_COLOR;
    } else {
      // Range mapping - find range that contains value
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      
      if (isNaN(numValue)) {
        return DEFAULT_COLOR;
      }

      const mapping = theme.mappings.find(m => {
        const min = m.min ?? -Infinity;
        const max = m.max ?? Infinity;
        return numValue >= min && numValue < max;
      });

      return mapping ? mapping.color : DEFAULT_COLOR;
    }
  }

  /**
   * Get legend items from theme
   */
  getLegendItems(theme: ColorTheme): Array<{ color: string; label: string }> {
    if (!theme || !theme.mappings) {
      return [];
    }

    return theme.mappings.map(mapping => ({
      color: mapping.color,
      label: mapping.label,
    }));
  }

  /**
   * Get cache statistics (for debugging)
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const themeCache = new ThemeCache();
export default themeCache;
